import { Head, router } from "@inertiajs/react"
import { Link as LinkIcon, Pencil, Trash2 } from "lucide-react"
import { useState } from "react"

import { FileIcon, HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import {
  companyResourcePath,
  companyResourcesPath,
  editCompanyResourcePath,
  newCompanyResourcePath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Resource {
  id: number
  name: string
  notes: string | null
  filename: string | null
  file_type: string | null
  file_url: string | null
  link: string | null
  date_added: string | null
  added_by: string | null
  status: number
}

interface ResourceGroup {
  id: number | null
  name: string
  resources: Resource[]
}

interface Props {
  resource_groups: ResourceGroup[]
  total_count: number
  can_create: boolean
  can_edit: boolean
  can_destroy: boolean
}

/* ── Columns ───────────────────────────────────────────── */

function buildColumns(
  canEdit: boolean,
  canDestroy: boolean,
): HpiColumn<Resource>[] {
  const cols: HpiColumn<Resource>[] = [
    {
      header: "Title",
      width: "max-w-[200px]",
      render: (row) => {
        const inactive = row.status === 1
        return row.file_url ? (
          <a
            href={row.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm font-medium hover:underline ${inactive ? "text-gray-400 line-through" : "text-hpi-dark-blue"}`}
          >
            {row.name}
          </a>
        ) : row.link ? (
          <a
            href={row.link}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm font-medium hover:underline ${inactive ? "text-gray-400 line-through" : "text-hpi-dark-blue"}`}
          >
            {row.name}
          </a>
        ) : (
          <div
            className={`text-sm font-medium ${inactive ? "text-gray-400 line-through" : "text-hpi-dark-gray"}`}
          >
            {row.name}
          </div>
        )
      },
    },
    {
      header: "Notes",
      render: (row) => (
        <div className="text-hpi-dark-gray text-sm">{row.notes ?? ""}</div>
      ),
    },
    {
      header: "File",
      width: "max-w-[120px]",
      render: (row) =>
        row.filename ? (
          <a
            href={row.file_url ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue flex items-center gap-1 text-sm hover:underline"
          >
            <FileIcon fileType={row.file_type} className="h-4 w-4" />
            <span className="truncate">{row.filename}</span>
          </a>
        ) : row.link ? (
          <a
            href={row.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue flex items-center gap-1 text-sm hover:underline"
          >
            <LinkIcon className="h-4 w-4" />
            <span>Link</span>
          </a>
        ) : (
          <span className="text-hpi-med-gray text-sm">-</span>
        ),
    },
    {
      header: "Added",
      width: "max-w-[120px]",
      render: (row) => (
        <div className="text-hpi-med-blue text-sm">{row.date_added ?? ""}</div>
      ),
    },
  ]

  if (canEdit) {
    cols.push({
      header: "",
      width: "max-w-[40px]",
      render: (row) => (
        <a
          href={editCompanyResourcePath(row.id)}
          className="text-hpi-warning hover:text-orange-600"
        >
          <Pencil className="h-4 w-4" />
        </a>
      ),
    })
  }

  if (canDestroy) {
    cols.push({
      header: "",
      width: "max-w-[40px]",
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation()
            if (confirm("Are you sure you want to delete this resource?")) {
              router.delete(companyResourcePath(row.id))
            }
          }}
          className="text-red-400 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ),
    })
  }

  return cols
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Company", href: companyResourcesPath() },
  { title: "Bios & Flyers", href: companyResourcesPath() },
]

export default function ResourcesIndex({
  resource_groups,
  total_count,
  can_create,
  can_edit,
  can_destroy,
}: Props) {
  const columns = buildColumns(can_edit, can_destroy)
  const [expandedGroups, setExpandedGroups] = useState<Set<number | null>>(
    new Set(resource_groups.map((g) => g.id)),
  )

  const toggleGroup = (id: number | null) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Bios & Flyers" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-hpi-med-blue text-sm">
            {total_count} resource{total_count !== 1 ? "s" : ""}
          </div>
          {can_create && (
            <a
              href={newCompanyResourcePath()}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-sm text-white"
            >
              Add Resource
            </a>
          )}
        </div>

        {resource_groups.length === 0 ? (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No resources found.
          </div>
        ) : (
          <div className="space-y-4">
            {resource_groups.map((group) => (
              <div
                key={group.id ?? "none"}
                className="border-hpi-light-gray rounded-lg border bg-white"
              >
                <button
                  className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                  onClick={() => toggleGroup(group.id)}
                >
                  <span className="text-hpi-dark-blue font-semibold">
                    {group.name}
                  </span>
                  <span className="text-hpi-med-blue text-sm">
                    {group.resources.length} item
                    {group.resources.length !== 1 ? "s" : ""}
                  </span>
                </button>

                {expandedGroups.has(group.id) && (
                  <div className="border-hpi-light-gray border-t">
                    <HpiDataTable<Resource>
                      title={group.name}
                      columns={columns}
                      data={group.resources}
                      keyFn={(row) => row.id}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
